import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { PrimaryButton, DangerButton, TextButton } from '../../styles/CommonStyles';
import { useRbac } from '../../hooks/useRbac';
import { useDeveloperOrg } from '../../hooks/useDeveloperOrg';
import { listRoles, deleteRole } from '../../services/rbacService';
import { useActingAccountId } from '../../hooks/useActingAccountId';
import { useTeamLiveRevision } from '../../hooks/useTeamLiveRevision';
import { subscribableKinds } from '../../services/teamLive/teamLivePolicy';
import RoleEditorModal from './RoleEditorModal';
import ConfirmDialog from './ConfirmDialog';
import type { RoleAppAccess } from './roleAppAccessShared';

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountRole {
  roleId: string;
  name: string;
  description?: string;
  system: boolean;
  status: 'active' | 'disabled';
  assignedMemberCount: number;
  grants: string[];
  effectivePermissions: string[];
  inheritsFrom: string[];
  templateKey: string | null;
  /** Round-16 connected-app ceilings (PLAN-connected-apps-governance-20260816 §5). */
  appAccess?: RoleAppAccess;
}

// ── Styled components ────────────────────────────────────────────────────────

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SectionLabel = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  text-transform: lowercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const RoleCard = styled.div`
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  }
`;

const RoleCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const RoleName = styled.div`
  font-weight: 600;
  color: var(--brand-black);
  font-size: 0.9375rem;
  text-transform: lowercase;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ $variant?: 'system' | 'count' | 'disabled' }>`
  border-radius: 20px;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  text-transform: lowercase;

  ${props => {
    switch (props.$variant) {
      case 'system':
        return `
          background: rgba(74, 74, 74, 0.08);
          color: var(--brand-text-secondary);
          border: 1px solid rgba(74, 74, 74, 0.2);
        `;
      case 'disabled':
        return `
          background: rgba(220, 53, 69, 0.08);
          color: #dc3545;
          border: 1px solid rgba(220, 53, 69, 0.2);
        `;
      default:
        return `
          background: rgba(198, 167, 94, 0.1);
          color: #9a7c32;
          border: 1px solid rgba(198, 167, 94, 0.3);
        `;
    }
  }}
`;

const RoleDescription = styled.p`
  font-size: 0.85rem;
  color: var(--brand-text-muted);
  margin: 0;
  line-height: 1.4;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const SmallBtn = styled(TextButton)`
  font-size: 0.8125rem;
  padding: 0.25rem 0.625rem;
`;

// Restrained danger (task #80): same quiet outline as MembersList — the
// scheme's #991B1B, not the loud filled red.
const SmallDangerBtn = styled(TextButton)`
  font-size: 0.8125rem;
  padding: 0.25rem 0.625rem;
  color: #991b1b;
  border: 1px solid rgba(153, 27, 27, 0.25);
  border-radius: 6px;

  &:hover {
    background: rgba(153, 27, 27, 0.06);
    color: #7f1d1d;
  }
`;

const PermCount = styled.span`
  font-size: 0.775rem;
  color: var(--brand-text-muted);
  white-space: nowrap;
`;

const InheritsBadge = styled.div`
  font-size: 0.775rem;
  color: var(--brand-text-muted);
  margin-top: 0.125rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--brand-text-muted);
  background: white;
  border: 1px dashed #ddd;
  border-radius: 10px;
  grid-column: 1 / -1;

  p {
    margin: 0 0 1rem;
    font-size: 0.9375rem;
  }
`;

const ErrorState = styled.div`
  padding: 1.25rem;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  color: #c53030;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// ── Component ────────────────────────────────────────────────────────────────

const RolesList: React.FC = () => {
  const { hasPermission } = useRbac();
  // Developer-org gate mirror (catalog v4): decides whether the editor shows
  // the Developer permission section. Server re-checks on save regardless.
  const { isDeveloperOrg } = useDeveloperOrg();
  const [roles, setRoles] = useState<AccountRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorTarget, setEditorTarget] = useState<AccountRole | null | 'new'>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AccountRole | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // RBAC-001: backend uses finer-grained roles.* perms. Gate shows/hides
  // create/edit/delete role controls — enable if the caller has any
  // role-mutation capability.
  const canManageRoles =
    hasPermission('roles.create') ||
    hasPermission('roles.update') ||
    hasPermission('roles.delete');

  // Background mode keeps the cards on screen through a live update or your
  // own save — only the first load shows the spinner.
  const load = useCallback(async (opts: { background?: boolean } = {}) => {
    const background = opts.background === true;
    if (!background) setLoading(true);
    setError(null);
    try {
      const data = await listRoles();
      setRoles(data.roles as AccountRole[]);
    } catch (err: any) {
      if (!background) setError(err?.message ?? 'failed to load roles');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live updates (2026-09-17). rbacListRoles is the one sibling callable that
  // returns no resolvedAccountId, so the acting organization is resolved from
  // the live back-pointer instead — the same tab-workspace ladder useRbac gates
  // this page on, so the two can never disagree about which org is on screen.
  // `members` rides along because the member COUNT on each card is recomputed
  // server-side from the member docs, so it moves when nobody touched a role.
  const actingAccountId = useActingAccountId();
  const liveKinds = useMemo(
    () =>
      subscribableKinds(['roles', 'members'], {
        hasTeamView: hasPermission('team.view'),
        hasRolesView: hasPermission('roles.view'),
      }),
    [hasPermission],
  );
  const liveRevision = useTeamLiveRevision({ accountId: actingAccountId, kinds: liveKinds });
  useEffect(() => {
    if (liveRevision > 0) void load({ background: true });
  }, [liveRevision, load]);

  // Styled confirmation instead of window.confirm/alert (the last native
  // dialogs on the team surface) — server rejections (e.g. the delegation
  // guard) render inside the dialog instead of a browser alert.
  const handleDelete = async (role: AccountRole) => {
    setDeleting(role.roleId);
    setDeleteError(null);
    try {
      await deleteRole(role.roleId);
      setDeleteTarget(null);
      await load({ background: true });
    } catch (err: any) {
      setDeleteError(err?.message ?? 'failed to delete role');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem 0', color: '#999', textAlign: 'center' }}>loading roles…</div>;
  }

  return (
    <>
      <Header>
        <SectionLabel>{roles.length} role{roles.length !== 1 ? 's' : ''}</SectionLabel>
        {canManageRoles && (
          <PrimaryButton onClick={() => setEditorTarget('new')}>+ create role</PrimaryButton>
        )}
      </Header>

      {error && (
        <ErrorState>
          <span>{error}</span>
          <SmallBtn onClick={() => { void load(); }}>retry</SmallBtn>
        </ErrorState>
      )}

      <Grid>
        {!error && roles.length === 0 && (
          <EmptyState>
            <p>no custom roles yet. create one to get started.</p>
            {canManageRoles && (
              <PrimaryButton onClick={() => setEditorTarget('new')}>create role</PrimaryButton>
            )}
          </EmptyState>
        )}

        {roles.map(role => (
          <RoleCard key={role.roleId}>
            <RoleCardHeader>
              <RoleName>{role.name}</RoleName>
              <BadgeRow>
                {role.system && <Badge $variant="system">system</Badge>}
                {role.status === 'disabled' && <Badge $variant="disabled">disabled</Badge>}
                <Badge>{role.assignedMemberCount ?? 0} member{(role.assignedMemberCount ?? 0) !== 1 ? 's' : ''}</Badge>
                <PermCount>{(role.effectivePermissions?.length ?? role.grants?.length ?? 0)} permissions</PermCount>
              </BadgeRow>
            </RoleCardHeader>

            {role.description && <RoleDescription>{role.description}</RoleDescription>}

            {(role.inheritsFrom?.length ?? 0) > 0 && (
              <InheritsBadge>
                ↑ inherits from {role.inheritsFrom.map(rid => roles.find(r => r.roleId === rid)?.name ?? rid).join(', ')}
              </InheritsBadge>
            )}

            {canManageRoles && (
              <CardActions>
                {/* System roles are platform-managed: name/permissions are
                    read-only, so the affordance is "view", never "edit" —
                    users must not think a system role's grants are editable. */}
                <SmallBtn
                  onClick={() => setEditorTarget(role)}
                  aria-label={role.system ? `view ${role.name}` : `edit ${role.name}`}
                >
                  {role.system ? 'view' : 'edit'}
                </SmallBtn>
                {!role.system && (
                  <SmallDangerBtn
                    onClick={() => { setDeleteError(null); setDeleteTarget(role); }}
                    disabled={deleting === role.roleId}
                  >
                    {deleting === role.roleId ? 'deleting…' : 'delete'}
                  </SmallDangerBtn>
                )}
              </CardActions>
            )}
          </RoleCard>
        ))}
      </Grid>

      {editorTarget !== null && (
        <RoleEditorModal
          role={editorTarget === 'new' ? null : editorTarget}
          allRoles={roles}
          onClose={() => setEditorTarget(null)}
          onSaved={() => { setEditorTarget(null); void load({ background: true }); }}
          developerOrg={isDeveloperOrg}
        />
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="delete role"
        message={
          deleteTarget
            ? `delete the role "${deleteTarget.name}"? this cannot be undone.`
            : ''
        }
        consequence="members holding only this role lose its permissions; their other roles are unaffected."
        confirmLabel="delete role"
        danger
        busy={deleteTarget !== null && deleting === deleteTarget.roleId}
        error={deleteError}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => { setDeleteTarget(null); setDeleteError(null); }}
      />
    </>
  );
};

export default RolesList;
